import cn from 'classnames';
import AnimatedImage from 'core-lib/components/AnimatedImage';
import { useComponentInViewport } from 'core-lib/hooks/useComponentInViewport';
import formatCurrency from 'core-lib/utils/currency';
import { formatFuelBalance } from 'core-lib/utils/fuel';
import { getImageKitUrl } from 'core-lib/utils/imageKit';
import { useEffect, useMemo, useRef } from 'react';
import { ProductCardProp } from 'src/modules/parkplusStoreV2/types/product';
import { trackProductViewedStoreV2 } from 'src/modules/parkplusStoreV2/utils/events';
import { RatingTag } from 'ui-v2/components/RatingTag';
import { Typography } from 'ui-v2/components/Typography';
import AddToCart from '../../../../AddToCart';
import s from './SmallStoreProductCard.module.scss';

export const SmallStoreProductCard = (productProp: ProductCardProp) => {
  const rootRef = useRef();
  const inViewPort = useComponentInViewport(rootRef, {
    threshold: 0.5,
  });

  const { product, id, position, section } = productProp;
  const isOutOfStock = useMemo(() => product.stock === 0, []);
  const productRef = useRef(false);

  useEffect(() => {
    if (inViewPort && !productRef.current) {
      trackProductViewedStoreV2(section, product?.productId, {
        description: product?.eventType,
      });
      productRef.current = true;
    }
  }, [
    section,
    inViewPort,
    id,
    product.eventType,
    product.productId,
    productProp?.product?.productId,
  ]);

  return (
    <div
      className={cn(s.root, {
        [s.outOfStock]: isOutOfStock,
      })}
      id={id}
      data-index={position}
      ref={rootRef}
    >
      <div className={s.imageContainer}>
        <AnimatedImage
          src={getImageKitUrl({ imageUrl: product?.coverImage, width: 400 })}
          className={s.coverImage}
          alt={product?.title}
        />
        {product?.tag && (
          <div className={s.tag}>
            <Typography
              label={product?.priceNudge ? product?.priceNudge : product?.tag}
              color="on_dark_bg"
              variant="tiny_bold"
              style={{ lineHeight: '12px', width: 'max-content' }}
              lineClamp={1}
            />
          </div>
        )}
        {product?.rating && (
          <div className={s.rating}>
            <RatingTag size="s" rating={+product?.rating} />
          </div>
        )}
        {isOutOfStock && (
          <div className={s.soldout}>
            <Typography
              label="Sold out"
              variant="body_3_medium"
              color="white_alpha_primary"
            />
          </div>
        )}
      </div>
      <div className={s.bottom}>
        <Typography
          label={product?.title}
          variant="label_4_medium"
          className={s.title}
        />
        <div className={s.money}>
          <Typography
            label={formatCurrency(product.loyaltyPrice)}
            variant="label_4_bold"
          />
          {product.loyaltyPrice !== product.price && <Typography
            label={formatCurrency(product?.price)}
            variant="label_4_regular"
            className={s.lineThrough}
            color="tertiary"
          />}
        </div>
        {product?.loyaltyPoints > 0 && product?.loyaltyPrice >= 0 && (
          <div className={s.mlPrice}>
            <Typography label={` using `} color="offer" variant="tiny_bold" />
            <AnimatedImage
              src="https://parkplus-bkt-img.parkplus.io/production/team/public/FS_20240104160730062768.png"
              className={s.drop}
              alt="Loyalty points"
            />
            <Typography
              label={formatFuelBalance(product?.loyaltyPoints)}
              color="offer"
              variant="tiny_bold"
            />
          </div>
        )}
      </div>
      <div className={s.addToCart}>
        <AddToCart product={product} isSmall section={section} />
      </div>
    </div>
  );
};
